import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, SlidersHorizontal, RotateCcw } from 'lucide-react';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import GenericSelect from '../ui/GenericSelect';
import AsyncGenericSelect from '../ui/AsyncGenericSelect';
import Button from '../ui/Button';
import GenericFormDrawer from '../GenericFormDrawer';
import { formatDateTime } from '../../utils/Utils';
import {
  COMPARE_NONE, ORG_VIEWS, PARTICIPANT_STATUSES, activeFilterKeys, clearFilter, defaultComparePeriod,
} from './pmAnalyticsFilters';

// PM Analitik Faz 2 (spec §6.2) — ortak süzgeç şeridi (İK Operasyon Panosu, E02). Şeritte dönem, organizasyon (çoklu departman),
// lokasyon (çoklu) ve son güncelleme; diğerleri "Filtreler" çekmecesinde. Etkin süzgeçler silinebilir etiketlerdir; "Sıfırla"
// dönemi koruyup diğerlerini kaldırır. Durum çağıranın (URL) elindedir — bileşen yalnız onChange çağırır.
const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.div`
  width: ${({ $w }) => $w || '220px'};
  max-width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme, $tone }) => ($tone === 'warning' ? theme.colors.auxiliar.warning : theme.colors.surface.border)};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const ChipRemove = styled(Button)`
  min-height: 0;
  padding: 0;
  line-height: 0;
`;

const rows = (res) => { const r = res?.data?.data; return Array.isArray(r) ? r : (r?.data || []); };

// Basit ad aramaları (departman/lokasyon/ünvan/grup) — GroupCriteriaBuilder'daki kanıtlanmış desen.
export const nameLookup = (endpoint, nameKey = 'name') => ({
  onSearch: async (q) => {
    const params = q ? { [`${nameKey}[like]`]: q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get(endpoint, { params });
    return rows(res).map((x) => ({ id: x.id, name: x[nameKey] }));
  },
  onGetById: async (id) => {
    try {
      const res = await api.get(`${endpoint}/${id}`);
      return { id, name: res?.data?.data?.[nameKey] || '' };
    } catch {
      return { id, name: '' };
    }
  },
});

export const employeeLookup = {
  onSearch: async (q) => {
    const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    return rows(res).map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() }));
  },
  onGetById: async (id) => {
    try {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res?.data?.data || {};
      return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() };
    } catch {
      return { id, name: '' };
    }
  },
};

// `extraChips` (Faz 2 Plan 4, spec §19/1): sayfaya özgü ek süzgeçlerin etiketleri — [{ key, label, onRemove }]; ortak etiketlerden
// sonra aynı satırda çizilir ve "Sıfırla" ile birlikte durur (Süreç Takibi Görevler sekmesinin görev süzgeçleri).
export default function PmAnalyticsFilterBar({
  periods, filters, onChange, onReset, lastUpdated, stale, groupMembership, extraChips = [],
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  // Kademe / iş ailesi süzgeci yalnız İK (sunucuyla aynı karar — spec 2026-10-27 §7.6). Oturum bağlamı yoksa İK sayılmaz.
  // İK = AuthContext.isHr (İK Rolü; superAdmin ve eski oturumda kurumsal yönetici dahil) — PmGradeGate ile aynı kural.
  const { isHr = false } = useAuth() || {};
  // Çekmecenin başlangıç değerleri AÇILDIĞI anda donar: pano yeniden çizildikçe (panellerin güncellik bildirimi) yeni nesne
  // gelseydi GenericFormDrawer formu sıfırlar, kullanıcının seçimleri silinirdi (son inceleme, Önemli 3).
  const [drawerInitial, setDrawerInitial] = useState(null);
  const drawerOpen = drawerInitial !== null;
  const setDrawerOpen = (open) => setDrawerInitial(open ? {
    managerEmployeeId: filters.managerEmployeeId,
    includeDescendants: filters.includeDescendants,
    titleIds: filters.titleIds,
    groupIds: filters.groupIds,
    gradeIds: filters.gradeIds || [],
    jobFamilyIds: filters.jobFamilyIds || [],
    participantStatuses: filters.participantStatuses,
    orgView: filters.orgView,
    comparePeriodId: filters.comparePeriodId || '',
  } : null);
  const departments = useMemo(() => nameLookup('/v1/departments'), []);
  const locations = useMemo(() => nameLookup('/v1/locations'), []);
  const titles = useMemo(() => nameLookup('/v1/titles'), []);
  const groups = useMemo(() => nameLookup('/v1/user-groups'), []);
  const grades = useMemo(() => nameLookup('/v1/job-grades'), []);
  const jobFamilies = useMemo(() => nameLookup('/v1/job-families'), []);

  const selected = (periods || []).find((p) => p.id === filters.periodId) || null;
  const autoCompare = defaultComparePeriod(periods, selected);
  const periodOptions = (periods || []).map((p) => ({ id: p.id, name: p.name }));
  const compareOptions = [
    { id: '', name: autoCompare ? t('performance.operations.filter.compareAuto', { name: autoCompare.name }) : t('performance.operations.filter.compareAutoNone') },
    { id: COMPARE_NONE, name: t('performance.operations.filter.compareNone') },
    ...(periods || []).filter((p) => p.id !== filters.periodId).map((p) => ({ id: p.id, name: p.name })),
  ];

  const schema = [
    { name: 'managerEmployeeId', label: t('performance.operations.filter.manager'), type: 'asyncSelect', grid: 12, ...employeeLookup },
    { name: 'includeDescendants', label: t('performance.operations.filter.includeDescendants'), type: 'checkbox', grid: 12 },
    { name: 'titleIds', label: t('performance.operations.filter.titles'), type: 'asyncSelect', isMulti: true, grid: 12, ...titles },
    { name: 'groupIds', label: t('performance.operations.filter.groups'), type: 'asyncSelect', isMulti: true, grid: 12, ...groups },
    ...(isHr ? [
      { name: 'gradeIds', label: t('performance.operations.filter.grades'), type: 'asyncSelect', isMulti: true, grid: 12, ...grades },
      { name: 'jobFamilyIds', label: t('performance.operations.filter.jobFamilies'), type: 'asyncSelect', isMulti: true, grid: 12, ...jobFamilies },
    ] : []),
    {
      name: 'participantStatuses', label: t('performance.operations.filter.participantStatus'), type: 'asyncSelect', isMulti: true, grid: 12, minChars: 0,
      onSearch: async () => PARTICIPANT_STATUSES.map((s) => ({ id: s, name: t(`performance.operations.participantStatus.${s}`) })),
      onGetById: async (id) => ({ id, name: t(`performance.operations.participantStatus.${id}`) }),
    },
    {
      name: 'orgView', label: t('performance.operations.filter.orgView'), type: 'select', grid: 12,
      options: ORG_VIEWS.map((v) => ({ id: v, name: t(`performance.operations.orgView.${v}`) })), idKey: 'id', labelKey: 'name',
    },
    {
      name: 'comparePeriodId', label: t('performance.operations.filter.comparePeriod'), type: 'select', grid: 12,
      options: compareOptions, idKey: 'id', labelKey: 'name',
    },
  ];

  const chipLabel = (key) => {
    const count = Array.isArray(filters[key]) ? filters[key].length : null;
    if (key === 'orgView') return t(`performance.operations.orgView.${filters.orgView}`);
    if (key === 'comparePeriodId') {
      const name = filters.comparePeriodId === COMPARE_NONE
        ? t('performance.operations.filter.compareNone')
        : (periods || []).find((p) => p.id === filters.comparePeriodId)?.name || '';
      return `${t('performance.operations.filter.comparePeriod')}: ${name}`;
    }
    const base = t(`performance.operations.filter.chip.${key}`);
    return count != null ? `${base} (${count})` : base;
  };

  const active = activeFilterKeys(filters);

  return (
    <>
      <Bar>
        <Field $w="260px">
          <GenericSelect
            label={t('performance.operations.filter.period')}
            value={filters.periodId}
            onChange={(v) => onChange({ ...filters, periodId: v })}
            options={periodOptions}
            idKey="id"
            labelKey="name"
            clearable={false}
          />
        </Field>
        <Field>
          <AsyncGenericSelect
            label={t('performance.operations.filter.departments')}
            value={filters.departmentIds}
            onChange={(v) => onChange({ ...filters, departmentIds: v || [] })}
            isMulti
            minChars={0}
            compact
            {...departments}
          />
        </Field>
        <Field>
          <AsyncGenericSelect
            label={t('performance.operations.filter.locations')}
            value={filters.locationIds}
            onChange={(v) => onChange({ ...filters, locationIds: v || [] })}
            isMulti
            minChars={0}
            compact
            {...locations}
          />
        </Field>
        <Button variant="outline" onClick={() => setDrawerOpen(true)}>
          <SlidersHorizontal size={16} />
          {' '}
          {t('performance.operations.filter.more')}
        </Button>
        <Meta>
          {lastUpdated ? <span data-meta="updated">{t('performance.operations.filter.lastUpdated', { time: formatDateTime(lastUpdated, lang) })}</span> : null}
          {stale ? <Tag $tone="warning" data-meta="stale">{t('performance.operations.filter.stale')}</Tag> : null}
          {filters.orgView === 'current' ? <Tag $tone="warning" data-meta="current-org">{t('performance.operations.filter.currentOrgNotice')}</Tag> : null}
          {groupMembership === 'current' && filters.groupIds.length ? <Tag data-meta="group-current">{t('performance.operations.filter.groupCurrent')}</Tag> : null}
        </Meta>
      </Bar>
      {active.length > 0 || extraChips.length > 0 ? (
        <Chips>
          {active.map((key) => (
            <Tag key={key} data-chip={key}>
              {chipLabel(key)}
              <ChipRemove variant="ghost" aria-label={t('performance.operations.filter.remove')} onClick={() => onChange(clearFilter(filters, key))}>
                <X size={12} />
              </ChipRemove>
            </Tag>
          ))}
          {extraChips.map((chip) => (
            <Tag key={`extra-${chip.key}`} data-chip={`task.${chip.key}`}>
              {chip.label}
              <ChipRemove variant="ghost" aria-label={t('performance.operations.filter.remove')} onClick={chip.onRemove}>
                <X size={12} />
              </ChipRemove>
            </Tag>
          ))}
          <Button variant="ghost" onClick={onReset} data-action="reset">
            <RotateCcw size={14} />
            {' '}
            {t('performance.operations.filter.reset')}
          </Button>
        </Chips>
      ) : null}
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('performance.operations.filter.more')}
        schema={schema}
        initialValues={drawerInitial || undefined}
        submitLabel={t('performance.operations.filter.apply')}
        onSave={async (values) => {
          onChange({
            ...filters,
            managerEmployeeId: values.managerEmployeeId || null,
            includeDescendants: values.includeDescendants !== false,
            titleIds: values.titleIds || [],
            groupIds: values.groupIds || [],
            gradeIds: isHr ? (values.gradeIds || []) : [],
            jobFamilyIds: isHr ? (values.jobFamilyIds || []) : [],
            participantStatuses: values.participantStatuses || [],
            orgView: values.orgView || 'snapshot',
            comparePeriodId: values.comparePeriodId || null,
          });
          return true;
        }}
      />
    </>
  );
}
